import { useState, useEffect, useCallback } from 'react';
import type { TimeEntry, ISODateString } from '../types/timesheet.types';
import { TimesheetStatus, toISODateString } from '../types/timesheet.types';
import type { SuperApprovalItem } from '../types/approval.types';
import { useConnector } from '../contexts/ConnectorContext';
import { WorkflowTriggerType } from '../types/connector.types';
import { buildPersonLookup, buildSuperApprovalCards } from './useSuperApprovals';
import { SP_LISTS } from '../utils/constants';
import { errorLogger } from '../utils/errorLogger';

interface State {
  items: SuperApprovalItem[];
  isLoading: boolean;
  loadError: Error | null;
  isActioning: boolean;
  actionError: Error | null;
}

export interface UseUnlockGroupTimesheetsResult {
  items: SuperApprovalItem[];
  isLoading: boolean;
  loadError: Error | null;
  isActioning: boolean;
  actionError: Error | null;
  unlockMonth: (item: SuperApprovalItem, reason: string, unlockedByName: string, unlockedByEmail: string) => Promise<void>;
  refresh: () => Promise<void>;
}

function listNameFor(source: 'employee' | 'asset'): string {
  return source === 'asset' ? SP_LISTS.ASSET_TIMESHEETS : SP_LISTS.TIME_ENTRIES;
}

export function useUnlockGroupTimesheets(): UseUnlockGroupTimesheetsResult {
  const connector = useConnector();

  const [state, setState] = useState<State>({
    items: [],
    isLoading: true,
    loadError: null,
    isActioning: false,
    actionError: null,
  });

  const load = useCallback(async () => {
    setState(s => ({ ...s, isLoading: true, loadError: null }));
    try {
      const [allProjects, allPeople, empEntries, astEntries] = await Promise.all([
        connector.getAllActiveProjects(),
        connector.getPeople({ active: true }),
        connector.getTimeEntries({ status: TimesheetStatus.SuperApproved }),
        connector.getTimeEntries({ status: TimesheetStatus.SuperApproved }, SP_LISTS.ASSET_TIMESHEETS),
      ]);

      const projectTypeMap = new Map(allProjects.map(p => [p.projectCode, { type: p.type }]));
      const personMap = buildPersonLookup(allPeople);
      const items = [
        ...buildSuperApprovalCards(empEntries as TimeEntry[], projectTypeMap, personMap, 'employee'),
        ...buildSuperApprovalCards(astEntries as TimeEntry[], projectTypeMap, personMap, 'asset'),
      ];

      setState(s => ({ ...s, items, isLoading: false }));
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      errorLogger.log(error, { component: 'useUnlockGroupTimesheets', action: 'load' });
      setState(s => ({ ...s, isLoading: false, loadError: error }));
    }
  }, [connector]);

  useEffect(() => { void load(); }, [load]);

  const unlockMonth = useCallback(
    async (item: SuperApprovalItem, reason: string, unlockedByName: string, unlockedByEmail: string): Promise<void> => {
      if (!reason.trim()) throw new Error('Unlock reason is required.');

      const now = toISODateString(new Date().toISOString().slice(0, 10));
      const listName = listNameFor(item.source);

      setState(s => ({ ...s, isActioning: true, actionError: null }));
      try {
        const allEntries = await connector.getTimeEntries(
          { personEmail: item.personEmail, periodMonth: item.periodMonth, status: TimesheetStatus.SuperApproved },
          listName,
        );
        const entries = item.source === 'asset'
          ? allEntries.filter(e => e.assetCode === item.assetCode)
          : allEntries;
        if (entries.length === 0) throw new Error('No Super Approved entries found to unlock.');

        await connector.batchUpdateEntries(
          entries.map(e => ({ id: e.id, fields: {
            status: TimesheetStatus.Draft,
            unlockedDate: now,
            unlockReason: reason,
            unlockApprovedBy: unlockedByName,
            lockedDate: null,
            superApprovedDate: null,
            managerApprovedDate: null,
            submittedDate: null,
          }})),
          listName
        );

        try {
          await connector.batchSaveAuditRecords([{
            timeEntryId: 0,
            personName: item.personName,
            personEmail: item.personEmail,
            dateWorked: `${item.periodMonth}-01` as ISODateString,
            periodMonth: item.periodMonth,
            oldValue: 'SuperApproved',
            newValue: 'Draft',
            editorName: unlockedByName,
            editorEmail: unlockedByEmail,
            editedDate: now,
            editReasonNotes: reason,
          }]);
        } catch (auditErr) {
          errorLogger.log(auditErr instanceof Error ? auditErr : new Error(String(auditErr)), {
            component: 'useUnlockGroupTimesheets',
            action: 'unlockMonth - audit',
            userEmail: unlockedByEmail,
          });
        }

        await connector.triggerWorkflow({
          triggerType: WorkflowTriggerType.UnlockMonth,
          personId: item.personId,
          userName: item.personName,
          userEmail: item.personEmail,
          periodMonth: item.periodMonth,
          actionStatus: 'Draft',
          isFinalState: false,
          approverName: unlockedByName,
          approverEmail: unlockedByEmail,
          approverRole: 'Unlock Group',
          reason,
          source: item.source === 'asset' ? 'AssetTimesheet' : 'UnlockGroup',
        });

        setState(s => ({
          ...s,
          items: s.items.filter(a =>
            !(a.personId === item.personId
              && a.periodMonth === item.periodMonth
              && a.source === item.source
              && a.assetCode === item.assetCode)
          ),
          isActioning: false,
        }));
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        errorLogger.log(error, { component: 'useUnlockGroupTimesheets', action: 'unlockMonth', userEmail: unlockedByEmail });
        setState(s => ({ ...s, isActioning: false, actionError: error }));
        throw error;
      }
    },
    [connector]
  );

  return {
    items: state.items,
    isLoading: state.isLoading,
    loadError: state.loadError,
    isActioning: state.isActioning,
    actionError: state.actionError,
    unlockMonth,
    refresh: load,
  };
}
